import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import "../styles/ReportPanelHeader.css";

interface ReportPanelHeaderProps {
  statusColor: string;
  statusTitle: string;
  onClose: () => void;
  onShare?: () => void;
  viewMode?: "report" | "cleaned";
  onViewModeChange?: (mode: "report" | "cleaned") => void;
  hasCleanedData?: boolean;
  reportImagesCount?: number;
  cleanedImagesCount?: number;
}

const ReportPanelHeader: React.FC<ReportPanelHeaderProps> = memo(
  ({
    statusColor,
    statusTitle,
    onClose,
    onShare,
    viewMode = "report",
    onViewModeChange,
    hasCleanedData = false,
    reportImagesCount = 0,
    cleanedImagesCount = 0,
  }) => {
    const { t } = useTranslation();

    const handleViewModeChange = (mode: "report" | "cleaned") => {
      if (mode === "cleaned" && !hasCleanedData) {
        return;
      }
      onViewModeChange?.(mode);
    };

    return (
      <div className="panel-header">
        <div className="panel-title">
          <div
            className="status-indicator"
            style={{ backgroundColor: statusColor }}
          />
          <h2>{statusTitle}</h2>
        </div>
        <div className="panel-header-actions">
          {/* View mode toggle - only show if there's cleaned data */}
          {hasCleanedData && onViewModeChange && (
            <div className="image-type-toggle">
              <button
                className={`toggle-btn ${
                  viewMode === "cleaned" ? "active" : ""
                }`}
                onClick={() => handleViewModeChange("cleaned")}
              >
                {t("report.cleanedImages")}
              </button>
              <button
                className={`toggle-btn ${
                  viewMode === "report" ? "active" : ""
                }`}
                onClick={() => handleViewModeChange("report")}
              >
                {t("report.reportImages")}
              </button>
            </div>
          )}
          {onShare && (
            <button
              className="share-button"
              onClick={onShare}
              aria-label={t("report.share")}
              title={t("report.share")}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.66 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
              </svg>
            </button>
          )}
          <button
            className="close-button"
            onClick={onClose}
            aria-label={t("common.closePanel")}
          >
            ×
          </button>
        </div>
      </div>
    );
  }
);

ReportPanelHeader.displayName = "ReportPanelHeader";

export default ReportPanelHeader;
